import { Component, Host, h, Element, Listen, Prop, Watch, Fragment, Method } from "@stencil/core";

import { StzhGalleryItem, Layout } from "../../index";

import { StzhGalleryLocalizedText } from "./stzh-gallery.localization";

import {} from "photoswipe";

import PhotoSwipeLightbox from "photoswipe/dist/photoswipe-lightbox.esm.js";
import PhotoSwipe from "photoswipe/dist/photoswipe.esm.js";
import PhotoSwipeDynamicCaption from "photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.esm.js";

import Close from "./assets/close.svg";
import AngleLeft from "./assets/angle-left.svg";
import AngleRight from "./assets/angle-right.svg";

const CLASS_LIGHTBOX = "stzh-gallery__pswp";
const CLASS_BODY_OPEN = "stzh-gallery-open";

type LightboxDataItem = {
  w: number;
  h: number;
  msrc: string;
  src: string;
  thumbCropped: boolean;
  captionHtml: string;
};

/**
 * @slot - Slot for custom content if `variant="custom"` is used, lightbox will query for stzh-figure (this.itemElement) elements
 */
@Component({
  tag: "stzh-gallery",
  styleUrl: "stzh-gallery.scss",
})
export class StzhGallery {
  /** Translation strings */
  @Prop() localization: StzhGalleryLocalizedText;

  /** Aspect ratio */
  @Prop({ reflect: true }) ratio: Layout.Ratio = "16:9";
  /** Aspect ratio above small breakpoint */
  @Prop({ reflect: true }) ratioSmall: Layout.Ratio | "inherit" = "inherit";
  /** Aspect ratio above medium breakpoint */
  @Prop({ reflect: true }) ratioMedium: Layout.Ratio | "inherit" = "inherit";
  /** Aspect ratio above large breakpoint */
  @Prop({ reflect: true }) ratioLarge: Layout.Ratio | "inherit" = "inherit";
  /** Aspect ratio above ultra breakpoint */
  @Prop({ reflect: true }) ratioUltra: Layout.Ratio | "inherit" = "inherit";

  /** Nr of images shown (only for grid variant) */
  @Prop({ mutable: true }) showItems: number = 6;
  private _initialShowItems: number;

  /** Whether indicator should be shown */
  @Prop() carouselWithTrack: boolean = false;

  /** Nr of items shown when more link is clicked (only for grid variant) */
  @Prop() stepItems: number = 6;

  /**
   * Images in gallery.
   * Array of objects that must at least include `width`, `height` and `src` attribute:
   * `[{"width": 2522, "height": 1075, "src": "media/images/example.jpg"}, {"width": 2050, "height": 1367, "src": "media/images/example2.jpg"}]`
   *
   * Possible other optional properties are:
   * `alt`: Alternative image description
   * / `srcLightbox`: `src` for img tag inside lightbox (overwrites `src`)
   * / `srcsetLightbox`: `srcset` for img tag inside lightbox
   * / `copyright`: Copyright inside caption
   * / `heading`: Heading inside caption
   * / `headingLightbox`: Alternative heading for lightbox (overwrites `heading`)
   * / `caption`: Caption
   * / `captionLightbox`: Caption for lightbox (overwrites `caption`)
   * / `lightboxHtml`: Alternative html content for lightbox caption
   * / `hideCounter`: Whether to hide counter
   */
  @Prop() images: StzhGalleryItem[] | string = [];
  private _images: StzhGalleryItem[];
  private _imagesShown: StzhGalleryItem[];
  private _imagesLightboxDataSource: LightboxDataItem[];
  private _showMoreLink: boolean = false;

  /**
   * Variant:
   * `carousel`: every image is displayed inside a carousel (default mode with more than on picture)
   * / `single`: only one image is shown without carousel functionality (other images only reachable by lightbox)
   * / `grid`: every image is displayed inside a grid
   * / `custom`: nothing is rendered, only lightbox will be initialized, `stzh-figure` (`this.itemElement`) can be slotted in
   */
  @Prop() variant: "carousel" | "single" | "grid" | "custom" = "carousel";

  /** Preview item that will be looked up and mapped to images in lightbox. */
  @Prop() itemElement: string = "stzh-figure";

  /** Whether carousel is disabled */
  @Prop({ reflect: true }) carouselDisabled: boolean = false;

  /**
   * Whether autoplay is enabled.
   * Gets paused, when user has focus inside carousel element or hovers over it.
   * Also when reduced motion media query is active or carousel is set disabled.
   * Gets disabled permanently, when user interacts with carousel manually (clicks prev/next, scrolls or drags)
   */
  @Prop({ mutable: true }) carouselAutoplay: boolean = false;

  /** Timeout delay in milliseconds used for autoplay */
  @Prop() carouselAutoplayTimeout: number = 3000;

  /**
   * Exclude first image in lightbox images (but still use as preview image).
   * Only used when `variant=single` is used.
   */
  @Prop() excludeFirstImage: boolean = false;

  @Element() element: HTMLStzhGalleryElement;

  @Method()
  async openLightbox(index: number = 0) {
    this.lightbox.loadAndOpen(index);
  }

  @Listen("appload", { target: "window" })
  apploadListener() {
    // resize if any stencil components are rendered when lightbox is open
    if (this.lightbox && this.lightbox.pswp && this.lightbox.pswp.isOpen) {
      this.lightbox.pswp.updateSize(true);
    }
  }

  @Watch("showItems")
  showItemsWatcher(newValue: number) {
    this._imagesShown = this._images.slice(0, newValue);
    this._showMoreLink = newValue < this._images.length;
  }

  @Watch("images")
  @Watch("excludeFirstImage")
  imagesWatcher(newValue: StzhGalleryItem[] | string) {
    if (typeof newValue === "string") {
      this._images = JSON.parse(newValue);
    } else {
      this._images = newValue;
    }

    this.showItemsWatcher(this._initialShowItems);

    const lightboxImages = this.excludeFirstImage ? this._images.slice(1) : this._images;

    this._imagesLightboxDataSource = lightboxImages.map((image, index) => {
      let captionHtml = "";

      const { lightboxHtml, heading, headingLightbox, caption, captionLightbox, copyright, hideCounter } = image;

      if (lightboxHtml !== undefined) {
        captionHtml = lightboxHtml;
      } else if (heading || caption || headingLightbox || captionLightbox || copyright || !hideCounter) {
        captionHtml = `
          <div class="pswp__caption-wrapper">
            <div class="pswp__heading-wrapper">
              <div class="pswp__caption-counter">${!hideCounter ? `${index + 1}/${lightboxImages.length}` : ""}</div>
              <div class="pswp__caption-heading">${headingLightbox || heading || ""}</div>
            </div>
            <div class="pswp__caption">
              <span class="pswp__caption-text">${captionLightbox || caption || ""}</span>
              <span class="pswp__caption-copyright">${copyright || ""}</span>
            </div>
          </div>
        `;
      }

      return {
        w: image.width,
        h: image.height,
        msrc: image.src,
        src: image.srcLightbox || image.src,
        srcset: image.srcsetLightbox,
        thumbCropped: true,
        captionHtml,
      };
    });
  }

  private lightbox: any;
  private carouselElement: HTMLStzhCarouselElement;
  private carouselActionsElement: HTMLElement;

  private openCurrentCarouselSlide = () => {
    this.lightbox.loadAndOpen(this.carouselElement.slide);
  };

  private handleMoreClick = () => {
    this.showItems = this.stepItems + this.showItems;
  };

  initLightbox() {
    if (this._imagesLightboxDataSource.length === 0) {
      return;
    }

    this.lightbox = new PhotoSwipeLightbox({
      // appendToEl: this.element,
      // gallery: this.element, // not needed if we supply dataSource
      dataSource: this._imagesLightboxDataSource,
      pswpModule: PhotoSwipe,
      arrowPrevSVG: AngleLeft,
      arrowNextSVG: AngleRight,
      closeSVG: Close,
      zoom: false,
      counter: false,
      loop: false,
      bgOpacity: 1,
      mainClass: CLASS_LIGHTBOX,
      closeOnVerticalDrag: false,
      clickToCloseNonZoomable: false,

      // https://photoswipe.com/adjusting-zoom-level/
      // initialZoomLevel: 'fit',
      // secondaryZoomLevel: 'fit',
      // maxZoomLevel: 'fit',
      // clickToCloseNonZoomable: true,

      // translations
      closeTitle: this.localization.closeTitle,
      zoomTitle: this.localization.zoomTitle,
      arrowPrevTitle: this.localization.arrowPrevTitle,
      arrowNextTitle: this.localization.arrowNextTitle,
      errorMsg: this.localization.errorMsg,
      indexIndicatorSep: "/",

      paddingFn: (_viewportSize, _itemData, _index) => {
        // const captionHeight = this.lightbox.pswp?.currSlide?.dynamicCaption?.element?.offsetHeight || 0;

        let top = 16 + 44 + 8;
        let bottom = 16;
        let side = 0;

        // console.log(viewportSize.x, window.innerWidth)

        if (window.innerWidth >= 1600) {
          // ultra
          top = 32 + 56 + 20;
          bottom = 32;
          side = 126 + 56 + 56;
        } else if (window.innerWidth >= 1260) {
          // large
          top = 32 + 56 + 20;
          bottom = 32;
          side = 64 + 56 + 56;
        } else if (window.innerWidth >= 900) {
          // medium
          top = 20 + 44 + 20;
          bottom = 20;
          side = 40 + 44 + 20;
        } else if (window.innerWidth >= 600) {
          // small
          top = 16 + 44 + 20;
          bottom = 16;
          side = 32 + 44 + 20;
        }

        // add safe space to prevent flickering
        // bottom = bottom + 20;

        // if (captionHeight > 0) {
        //   bottom = 20;
        // }

        return {
          top,
          bottom,
          left: side,
          right: side,
        };
      },
    });

    let firstHidden;
    let lastHidden;

    const showHiddenDuringAnimation = slide => {
      this.carouselActionsElement?.classList.remove("is-hidden-during-animation");
      slide?.classList.remove("is-hidden-during-animation");
    };

    const hideHiddenDuringAnimation = slide => {
      this.carouselActionsElement?.classList.add("is-hidden-during-animation");
      slide?.classList.add("is-hidden-during-animation");
    };

    const stopPropagation = event => {
      event.stopPropagation();
    };

    const updateSize = () => {
      this.lightbox.pswp.updateSize(true);
    };

    const slides = Array.from(this.element.querySelectorAll(this.itemElement));

    this.lightbox.addFilter("itemData", (itemData, index: number) => {
      itemData.element = slides[index];
      return itemData;
    });

    this.lightbox.addFilter("thumbEl", (thumbEl, data, _index) => {
      const el = data.element?.querySelector("img") as HTMLImageElement;

      if (el) {
        return el;
      }

      return thumbEl;
    });

    this.lightbox.addFilter("numItems", () => {
      return this._imagesLightboxDataSource.length;
    });

    this.lightbox.on("afterInit", () => {
      firstHidden = this.lightbox.pswp.currSlide.data.element;
      lastHidden = this.lightbox.pswp.currSlide.data.element;
      hideHiddenDuringAnimation(firstHidden);

      if (this._imagesLightboxDataSource.length === 1) {
        this.lightbox.pswp.element.classList.add("pswp--one-image");
      }
    });

    this.lightbox.on("beforeOpen", () => {
      document.body.classList.add(CLASS_BODY_OPEN);
    });

    this.lightbox.on("close", () => {
      document.body.classList.remove(CLASS_BODY_OPEN);

      // can be null in "single" variant, when not every slide is in carousel or grid
      if (this.lightbox.pswp.currSlide.data.element) {
        lastHidden = this.lightbox.pswp.currSlide.data.element;
      }

      if (lastHidden !== firstHidden) {
        showHiddenDuringAnimation(firstHidden);
        hideHiddenDuringAnimation(lastHidden);
      }
    });

    this.lightbox.on("destroy", () => {
      showHiddenDuringAnimation(lastHidden);
    });

    this.lightbox.on("change", () => {
      // update carousel to same slide the lightshow shows
      this.carouselElement?.slideTo(this.lightbox.pswp.currIndex, "auto");
      // enable scroll for caption div
      if (
        this.lightbox.pswp.currSlide.dynamicCaption.element !== undefined &&
        this.lightbox.pswp.currSlide.dynamicCaption.element.innerText.length > 0
      ) {
        this.lightbox.pswp.currSlide.dynamicCaption.element.addEventListener("scroll", stopPropagation);
        this.lightbox.pswp.currSlide.dynamicCaption.element.addEventListener("wheel", stopPropagation);
      }
    });

    // this.lightbox.addFilter('preventPointerEvent', (preventPointerEvent, originalEvent, pointerType) => {
    //   // return true to preventDefault pointermove/pointerdown events
    //   // (also applies to touchmove/mousemove)
    //   console.log(preventPointerEvent, originalEvent, pointerType);
    //   return false;
    // });

    this.lightbox.on("bindEvents", () => {
      this.lightbox.pswp.element.addEventListener("stzhClamp", updateSize);
      this.lightbox.pswp.element.addEventListener("stzhExpand", updateSize);
      this.lightbox.pswp.element.addEventListener("stzhOpen", updateSize);
      this.lightbox.pswp.element.addEventListener("stzhOpened", updateSize);
      this.lightbox.pswp.element.addEventListener("stzhClose", updateSize);
      this.lightbox.pswp.element.addEventListener("stzhClosed", updateSize);
    });

    new PhotoSwipeDynamicCaption(this.lightbox, {
      type: "below",
      captionContent: slide => {
        // '.stzh-gallery__caption-content'
        return slide.data.captionHtml;
      },
      mobileLayoutBreakpoint: () => {
        return window.innerWidth < 600;
      },
      // if enabled, the image will always be vertically centered in the remaining space between the caption and the rest of the viewport.
      // If set to false (default value) - the image will lift up only if the caption does not fit below.
      // verticallyCenterImage: false,

      // when the caption x position is less than this value, it'll get class pswp__dynamic-caption--on-hor-edge. You may use it to apply different styling, such as horizontal padding.
      // horizontalEdgeThreshold: 20

      // a ratio defines the amount of horizontal empty space before the mobile caption switches to an "overlap" layout.
      // For example, if it's set to 0.3 - the caption will start overlapping the image when more than 30% of horizontal space is not occupied by an image. If you set it to 0 - the caption will always overlap. If you set it to 1 - the caption will constantly shift the image (unless it's taller than the viewport).
      mobileCaptionOverlapRatio: 1,
    });

    this.lightbox.init();
  }

  // componentShouldUpdate(_newValue, _oldValue, prop: string) {
  //   return ["isOpen"].includes(prop) === false;
  // }

  async componentWillLoad() {
    this._initialShowItems = this.showItems;
    this.imagesWatcher(this.images);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "gallery");
    }
  }

  async componentWillUpdate() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }

  componentDidRender() {
    if (!this.lightbox) {
      this.initLightbox();
    }
  }

  disconnectedCallback() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }

  private renderFigure(image: StzhGalleryItem, index: number): HTMLStzhFigureElement {
    const total = this._imagesLightboxDataSource.length;

    return (
      <stzh-figure
        class="stzh-gallery__slide"
        caption={image.caption}
        heading={image.heading}
        counter={
          !image.hideCounter && !(this.variant === "single" && this.excludeFirstImage) ? `${index + 1}/${total}` : ""
        }
      >
        <stzh-ratio
          ratio={this.ratio}
          ratioSmall={this.ratioSmall}
          ratioMedium={this.ratioMedium}
          ratioLarge={this.ratioLarge}
          ratioUltra={this.ratioUltra}
          onClick={event => {
            event.preventDefault();
            this.openLightbox(index);
          }}
        >
          <img
            loading="lazy"
            src={image.src}
            alt={(image.alt || this.localization.defaultAlt || "")
              .replace(/\{current\}/, (index + 1).toString())
              .replace(/\{total\}/, total.toString())}
          />

          {(this.variant === "single" || this.variant === "grid" || this._images.length === 1) && (
            <stzh-hspace class="stzh-gallery__actions" justify="end" items="end" size="xsmall">
              <stzh-button
                variant="secondary"
                size="small"
                icon="expand"
                label={this.localization.openLightbox}
                icon-only
                onClick={() => {
                  this.openLightbox(index);
                }}
              ></stzh-button>
            </stzh-hspace>
          )}
        </stzh-ratio>
      </stzh-figure>
    );
  }

  render() {
    const classes = {
      "stzh-gallery": true,
      [`stzh-gallery--${this.variant}`]: !!this.variant,
    };

    return (
      <Host>
        <div class={classes}>
          {(this.variant === "single" || this._images.length === 1) && (
            <Fragment>{this.renderFigure(this._images[0], 0)}</Fragment>
          )}

          {this.variant === "grid" && this._images.length > 0 && (
            <Fragment>
              <div class="stzh-gallery__list">
                {this._imagesShown.map((image, index) => this.renderFigure(image, index))}
              </div>

              {this._showMoreLink && (
                <div class="stzh-gallery__list-actions">
                  <stzh-button
                    variant="secondary"
                    label={this.localization.showMore}
                    onClick={this.handleMoreClick}
                  ></stzh-button>
                </div>
              )}
            </Fragment>
          )}

          {this.variant === "carousel" && this._images.length > 1 && (
            <stzh-carousel
              ref={el => (this.carouselElement = el as HTMLStzhCarouselElement)}
              disabled={this.carouselDisabled}
              autoplay={this.carouselAutoplay}
              autoplayTimeout={this.carouselAutoplayTimeout}
              withTrack={this.carouselWithTrack}
              variant="default"
            >
              <stzh-ratio
                ref={el => (this.carouselActionsElement = el as HTMLElement)}
                slot="actions"
                class="stzh-gallery__carousel-actions"
                ratio={this.ratio}
                ratioSmall={this.ratioSmall}
                ratioMedium={this.ratioMedium}
                ratioLarge={this.ratioLarge}
                ratioUltra={this.ratioUltra}
              >
                <div>
                  <stzh-button
                    class="stzh-carousel-action-previous"
                    data-stzh-carousel-previous
                    variant="secondary"
                    size="small"
                    icon="angle-left"
                    label={this.localization.arrowPrevTitle}
                    icon-only
                  ></stzh-button>
                  <stzh-button
                    class="stzh-carousel-action-next"
                    data-stzh-carousel-next
                    variant="secondary"
                    size="small"
                    icon="angle-right"
                    label={this.localization.arrowNextTitle}
                    icon-only
                  ></stzh-button>
                </div>
                <stzh-hspace justify="end" items="end" size="xsmall">
                  <stzh-button
                    variant="secondary"
                    size="small"
                    icon="expand"
                    icon-only
                    label={this.localization.openLightbox}
                    onClick={this.openCurrentCarouselSlide}
                  ></stzh-button>
                </stzh-hspace>
              </stzh-ratio>

              {this._images.map((image, index) => this.renderFigure(image, index))}
            </stzh-carousel>
          )}

          <slot></slot>
        </div>
      </Host>
    );
  }
}
